// frontend/src/pages/accessProvisioning.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getOrganizations, ensureAdminUser, removeCustomerUser, reactivateCustomerUser } from "../api/accessProvisioning";

export default function AccessProvisioning() {
  // Orgs
  const [orgs, setOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [orgError, setOrgError] = useState("");

  // Selection + filter
  const [search, setSearch] = useState("");
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Form
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sendPasswordReset, setSendPasswordReset] = useState(true);

  // Submit state
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  // Remove user state
  const [removeEmail, setRemoveEmail] = useState("");
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState("");
  const [removeResult, setRemoveResult] = useState(null);

  // Reactivate user state
  const [reactivateEmail, setReactivateEmail] = useState("");
  const [reactivateBusy, setReactivateBusy] = useState(false);
  const [reactivateError, setReactivateError] = useState("");
  const [reactivateResult, setReactivateResult] = useState(null);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoadingOrgs(true);
      setOrgError("");
      try {
        const data = await getOrganizations();
        if (ignore) return;

        const sorted = (Array.isArray(data) ? data : [])
          .slice()
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        setOrgs(sorted);
        if (!selectedOrg && sorted.length) setSelectedOrg(sorted[0]);
      } catch (err) {
        if (ignore) return;
        setOrgError(`Failed to load organizations${err?.message ? ` — ${err.message}` : ""}`);
      } finally {
        if (!ignore) setLoadingOrgs(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredOrgs = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return orgs;
    return orgs.filter((o) => (o.name || "").toLowerCase().includes(q));
  }, [orgs, search]);

  const canSubmit = !!selectedOrg?.id && email.trim().length > 0 && !busy;

  async function onSubmit(e) {
    e.preventDefault();
    if (!selectedOrg?.id) return;

    setBusy(true);
    setSubmitError("");
    setResult(null);

    try {
      const payload = {
        email: email.trim().toLowerCase(),
        name: name.trim() || undefined,
        sendPasswordReset: Boolean(sendPasswordReset),
      };

      const data = await ensureAdminUser(selectedOrg.id, payload);
      setResult(data);
    } catch (err) {
      setSubmitError(err?.message || "Request failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRemove(e) {
    e.preventDefault();
    if (!selectedOrg?.id || !removeEmail.trim()) return;

    setRemoveBusy(true);
    setRemoveError("");
    setRemoveResult(null);

    try {
      const data = await removeCustomerUser(selectedOrg.id, removeEmail.trim().toLowerCase());
      setRemoveResult(data);
    } catch (err) {
      setRemoveError(err?.message || "Request failed");
    } finally {
      setRemoveBusy(false);
    }
  }

  async function onReactivate(e) {
    e.preventDefault();
    if (!selectedOrg?.id || !reactivateEmail.trim()) return;

    setReactivateBusy(true);
    setReactivateError("");
    setReactivateResult(null);

    try {
      const data = await reactivateCustomerUser(selectedOrg.id, reactivateEmail.trim().toLowerCase());
      setReactivateResult(data);
    } catch (err) {
      setReactivateError(err?.message || "Request failed");
    } finally {
      setReactivateBusy(false);
    }
  }

  return (
    <div className="row">
      {/* Left pane - Organizations Panel (~30%) */}
      <aside className="col-12 col-md-4 col-lg-3 mb-3">
        <div className="card shadow-sm" style={{ borderRadius: "8px" }}>
          <div className="card-body">
            <h5 className="mb-3" style={{ fontWeight: 500 }}>Organizations</h5>
            
            {/* Filter input */}
            <div className="mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Filter organizations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={loadingOrgs}
                style={{ borderRadius: "6px" }}
              />
            </div>

            {/* Organization dropdown selector */}
            <div className="mb-2">
              <label className="form-label">Select organization</label>
              {loadingOrgs ? (
                <div className="text-muted small">Loading organizations…</div>
              ) : orgError ? (
                <div className="text-danger small">{orgError}</div>
              ) : (
                <select
                  className="form-select"
                  value={selectedOrg?.id != null ? String(selectedOrg.id) : ""}
                  onChange={(e) => {
                    const org = orgs.find((o) => String(o.id) === e.target.value);
                    if (org) {
                      setSelectedOrg(org);
                      setSubmitError("");
                      setResult(null);
                    }
                  }}
                  disabled={loadingOrgs || filteredOrgs.length === 0}
                  style={{ borderRadius: "6px" }}
                >
                  {filteredOrgs.length === 0 ? (
                    <option value="">No organizations found</option>
                  ) : (
                    filteredOrgs.map((o) => (
                      <option key={o.id} value={String(o.id)}>
                        {o.name}
                      </option>
                    ))
                  )}
                </select>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Right pane - User Management Panel (~70%) */}
      <main className="col-12 col-md-8 col-lg-9">
        <div className="card shadow-sm" style={{ borderRadius: "8px" }}>
          <div className="card-body">
            <h5 className="mb-4" style={{ fontWeight: 500 }}>
              User Management {selectedOrg ? `— ${selectedOrg.name}` : ""}
            </h5>
            
            {!selectedOrg ? (
              <div className="text-muted">Select an organization to begin.</div>
            ) : (
              <>
                <form onSubmit={onSubmit} className="mb-3">
                  {/* Email Field */}
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="user@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={busy}
                      required
                      style={{ borderRadius: "6px" }}
                    />
                  </div>

                  {/* Name Field */}
                  <div className="mb-3">
                    <label className="form-label">Name (optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Jane Doe"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={busy}
                      style={{ borderRadius: "6px" }}
                    />
                  </div>

                  {/* Checkbox */}
                  <div className="form-check mb-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="sendReset"
                      checked={sendPasswordReset}
                      onChange={(e) => setSendPasswordReset(e.target.checked)}
                      disabled={busy}
                    />
                    <label className="form-check-label" htmlFor="sendReset">
                      Send onboarding email (reset/invite)
                    </label>
                  </div>

                  {/* Primary Action Button */}
                  <button 
                    className="btn btn-dark" 
                    type="submit" 
                    disabled={!canSubmit}
                    style={{ borderRadius: "6px", paddingLeft: "2rem", paddingRight: "2rem" }}
                  >
                    {busy ? "Working…" : "Ensure Admin User"}
                  </button>
                </form>

                {submitError && (
                  <div className="alert alert-danger py-2" role="alert">
                    {submitError}
                  </div>
                )}

                {result && <EnsureAdminResult result={result} />}

                {/* Remove User from Customer DB */}
                <hr className="my-4" />
                <h6 className="mb-3" style={{ fontWeight: 500 }}>Remove User from Customer DB</h6>
                <form onSubmit={onRemove} className="mb-3">
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="user@company.com"
                      value={removeEmail}
                      onChange={(e) => setRemoveEmail(e.target.value)}
                      disabled={removeBusy}
                      required
                      style={{ borderRadius: "6px" }}
                    />
                  </div>
                  <button
                    className="btn btn-outline-danger"
                    type="submit"
                    disabled={!selectedOrg?.id || !removeEmail.trim() || removeBusy}
                    style={{ borderRadius: "6px", paddingLeft: "2rem", paddingRight: "2rem" }}
                  >
                    {removeBusy ? "Working…" : "Remove User"}
                  </button>
                </form>

                {removeError && (
                  <div className="alert alert-danger py-2" role="alert">
                    {removeError}
                  </div>
                )}

                {removeResult && (
                  <div className="alert alert-success py-2" role="alert">
                    {removeResult.rows_updated > 0
                      ? `User "${removeResult.email}" marked as deleted (${removeResult.rows_updated} row updated).`
                      : `No user found with email "${removeResult.email}" in this organization's DB.`}
                  </div>
                )}

                {/* Reactivate User in Customer DB */}
                <hr className="my-4" />
                <h6 className="mb-3" style={{ fontWeight: 500 }}>Reactivate User in Customer DB</h6>
                <form onSubmit={onReactivate} className="mb-3">
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="user@company.com"
                      value={reactivateEmail}
                      onChange={(e) => setReactivateEmail(e.target.value)}
                      disabled={reactivateBusy}
                      required
                      style={{ borderRadius: "6px" }}
                    />
                  </div>
                  <button
                    className="btn btn-outline-success"
                    type="submit"
                    disabled={!selectedOrg?.id || !reactivateEmail.trim() || reactivateBusy}
                    style={{ borderRadius: "6px", paddingLeft: "2rem", paddingRight: "2rem" }}
                  >
                    {reactivateBusy ? "Working…" : "Reactivate User"}
                  </button>
                </form>

                {reactivateError && (
                  <div className="alert alert-danger py-2" role="alert">
                    {reactivateError}
                  </div>
                )}

                {reactivateResult && (
                  <div className="alert alert-success py-2" role="alert">
                    {reactivateResult.rows_updated > 0
                      ? `User "${reactivateResult.email}" reactivated (${reactivateResult.rows_updated} row updated).`
                      : `No user found with email "${reactivateResult.email}" in this organization's DB.`}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function EnsureAdminResult({ result }) {
  const rawProvider = (result?.identity_provider?.provider || result?.customer_db?.auth_provider || "");
  const provider = rawProvider.toUpperCase();
  // Entra ID accounts live in the directory; this tool only reconciles the customer DB,
  // so the provider panel has nothing of its own to report.
  const isEntra = rawProvider.toLowerCase() === "entraid";
  const status = (result?.status || "").toLowerCase();
  const fallbackMessage =
    status === "noop"
      ? "User exists and already has Admin access."
      : null;
  const warnings = (Array.isArray(result?.errors) ? result.errors : []).filter(Boolean);
  return (
    <div className="card border-0 bg-light">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start gap-3">
          <div>
            <div className="fw-bold">Result: {result.status || "—"}</div>
            {(result.message || fallbackMessage) && (
              <div className="text-muted small mt-1" style={{ whiteSpace: "pre-wrap" }}>
                {result.message || fallbackMessage}
              </div>
            )}
          </div>
          <div className="text-muted small">{result.email}</div>
        </div>

        <div className="row mt-3 g-3">
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm">
              <div className="card-header py-2">Identity Provider {provider ? `— ${provider}` : ""}</div>
              <div className="card-body">
                {isEntra ? (
                  <div className="text-muted small">
                    Entra ID accounts are created and invited in the directory. This tool
                    made no change there — only the customer database was reconciled.
                  </div>
                ) : (
                  <>
                    <KV label="user_created" value={result.identity_provider?.user_created} />
                    <KV label="membership_added" value={result.identity_provider?.membership_added} />
                    <KV label="invitation_sent" value={result.identity_provider?.invitation_sent} />
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card shadow-sm">
              <div className="card-header py-2">Customer DB</div>
              <div className="card-body">
                <KV label="user_existed" value={result.customer_db?.user_existed} />
                <KV label="user_created" value={result.customer_db?.user_created} />
                <KV
                  label="admin_role_added"
                  value={
                    result.customer_db?.admin_role_unconfirmed
                      ? "unconfirmed"
                      : result.customer_db?.admin_role_added
                  }
                />
                {result.customer_db?.user_is_deleted && (
                  <KV label="user_is_deleted" value={true} />
                )}
                {result.customer_db?.user_state_unconfirmed && (
                  <KV label="user_is_deleted" value="unknown" />
                )}
              </div>
            </div>
          </div>
        </div>

        {result.customer_db?.user_is_deleted && (
          <div className="alert alert-danger py-2 mt-3 mb-0" role="alert">
            This user exists but its state is <strong>deleted</strong>, so it cannot
            sign in. Use Reactivate User below.
          </div>
        )}

        {warnings.length > 0 && (
          <div className="alert alert-warning py-2 mt-3 mb-0" role="alert">
            {warnings.map((w, i) => (
              <div key={i}>{w?.warning || (typeof w === "string" ? w : JSON.stringify(w))}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  const v = value === true ? "true" : value === false ? "false" : value ?? "—";
  return (
    <div className="d-flex justify-content-between gap-3 mb-1">
      <div className="text-muted">{label}</div>
      <div className="fw-semibold">{String(v)}</div>
    </div>
  );
}
